import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  PersonOff as PersonOffIcon,
  ArrowBack as ArrowBackIcon,
  ViewCompact as ViewCompactIcon,
  ViewStream as ViewStreamIcon,
  ViewDay as ViewDayIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { alpha } from '@mui/material/styles';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

const ShiftManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [shifts, setShifts] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [shiftTherapists, setShiftTherapists] = useState({});
  const [selectedShift, setSelectedShift] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [openNewShiftDialog, setOpenNewShiftDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState('');
  const [editedShift, setEditedShift] = useState(null);
  const [selectedTherapists, setSelectedTherapists] = useState([]);
  const [conflictingTherapists, setConflictingTherapists] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [assignments, setAssignments] = useState({});

  const [newShift, setNewShift] = useState({
    name: '',
    shift_code: '',
    start_time: '09:00',
    end_time: '17:00',
    breaks: [
      { start: '11:00', end: '11:30' },
      { start: '14:00', end: '14:30' },
    ],
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    description: '',
    color: '#C7AE6E', // Default color (brand gold)
    availability_type: 'AVAILABLE', // AVAILABLE or UNAVAILABLE
  });

  const [viewMode, setViewMode] = useState('normal'); // 'compact', 'medium', 'normal'

  const getViewStyles = (mode, shiftColor) => {
    switch (mode) {
      case 'compact':
        return {
          container: { minWidth: 200 },
          paper: { p: 1, minHeight: '40px' },
          icon: { fontSize: 16 },
          text: { fontSize: '0.75rem' },
          heading: { fontSize: '0.875rem' },
          subheading: { fontSize: '0.75rem' },
          grid: { spacing: 0.5 },
          therapistItem: {
            p: 0.5,
            borderRadius: 0.5,
            '&:hover': {
              bgcolor: alpha(shiftColor || '#000', 0.05),
            }
          }
        };
      case 'medium':
        return {
          container: { minWidth: 250 },
          paper: { p: 1.25, minHeight: '50px' },
          icon: { fontSize: 18 },
          text: { fontSize: '0.875rem' },
          heading: { fontSize: '1rem' },
          subheading: { fontSize: '0.875rem' },
          grid: { spacing: 0.75 },
          therapistItem: {
            p: 1,
            borderRadius: 0.75,
            '&:hover': {
              bgcolor: alpha(shiftColor || '#000', 0.05),
            }
          }
        };
      default: // normal
        return {
          container: { minWidth: 300 },
          paper: { p: 1.5, minHeight: '60px' },
          icon: { fontSize: 20 },
          text: { fontSize: '1rem' },
          heading: { fontSize: '1.25rem' },
          subheading: { fontSize: '1rem' },
          grid: { spacing: 1 },
          therapistItem: {
            p: 1.5,
            borderRadius: 1,
            '&:hover': {
              bgcolor: alpha(shiftColor || '#000', 0.05),
            }
          }
        };
    }
  };

  useEffect(() => {
    fetchShifts();
    fetchTherapists();
    fetchShiftTherapists();
    fetchAssignments();
  }, []);

  useEffect(() => {
    const d = selectedDate?.format('YYYY-MM-DD');
    console.log('Date or shift changed:', { date: d, selectedShift });
    if (d) {
      fetchAssignmentsForDate(d);
    }
  }, [selectedDate, shifts, selectedShift]);

  const fetchAssignments = async () => {
    try {
      const response = await apiGet('/api/roster/assignments');
      if (!response.ok) {
        throw new Error('Failed to fetch assignments');
      }
      const data = await response.json();
      console.log('Fetched all assignments:', data);
      setAssignments(data || {});
    } catch (error) {
      console.error('Error fetching assignments:', error);
      enqueueSnackbar('Failed to fetch assignments', { variant: 'error' });
    }
  };

  const fetchAssignmentsForDate = async (dateStr) => {
    try {
      const response = await apiGet(`/api/roster/assignments?start_date=${dateStr}&end_date=${dateStr}`);
      if (!response.ok) throw new Error('Failed to fetch assignments');
      const data = await response.json();
      console.log('Fetched assignments (range):', data);
      setAssignments(data || {});
    } catch (error) {
      console.error('Error fetching assignments (range):', error);
      enqueueSnackbar('Failed to fetch assignments', { variant: 'error' });
    }
  };

  const fetchShifts = async () => {
    try {
      console.log('Fetching shifts...');
      const response = await apiGet('/api/shifts');
      if (!response.ok) {
        throw new Error('Failed to fetch shifts');
      }
      const data = await response.json();
      console.log('Fetched shifts:', data);
      setShifts(data);
      
      // Select first shift by default if none selected
      if (!selectedShift && data.length > 0) {
        setSelectedShift(data[0].shift_code);
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
      enqueueSnackbar('Failed to fetch shifts', { variant: 'error' });
    }
  };

  const fetchTherapists = async () => {
    try {
      const response = await apiGet('/api/therapists');
      if (!response.ok) throw new Error('Failed to fetch therapists');
      const data = await response.json();
      console.log('Fetched therapists:', data);
      setTherapists(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      setTherapists([]);
    }
  };

  const fetchShiftTherapists = async () => {
    try {
      const response = await apiGet('/api/shifts/therapists');
      if (!response.ok) throw new Error('Failed to fetch shift therapists');
      const data = await response.json();
      console.log('Fetched shift therapists:', data);
      setShiftTherapists(data || {});
    } catch (error) {
      console.error('Error fetching shift therapists:', error);
      setShiftTherapists({});
    }
  };

  const handleCreateShift = async () => {
    try {
      const response = await apiPost('/api/shifts', newShift);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create shift');
      }
      
      setOpenNewShiftDialog(false);
      fetchShifts(); // Refresh shifts
      
      // Reset form
      setNewShift({
        name: '',
        shift_code: '',
        start_time: '09:00',
        end_time: '17:00',
        breaks: [
          { start: '11:00', end: '11:30' },
          { start: '14:00', end: '14:30' },
        ],
        working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        description: '',
        color: '#1976d2', // Default color
        availability_type: 'AVAILABLE',
      });
    } catch (error) {
      console.error('Error creating shift:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleUpdateShift = async () => {
    console.log('handleUpdateShift called');
    console.log('selectedShift:', selectedShift);
    console.log('editedShift:', editedShift);
    
    if (!selectedShift || !editedShift) {
      console.log('Missing selectedShift or editedShift');
      return;
    }
    
    try {
      // Ensure working_days is an array before sending
      const shiftData = {
        ...editedShift,
        working_days: Array.isArray(editedShift.working_days) ? editedShift.working_days : []
      };
      
      console.log('Sending shift update:', shiftData);
      
      const response = await apiPut(`/api/shifts/${selectedShift}`, shiftData);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update shift');
      }
      
      const result = await response.json();
      console.log('Update response:', result);
      
      setEditMode(false);
      fetchShifts(); // Refresh shifts
      enqueueSnackbar('Shift updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating shift:', error);
      enqueueSnackbar(error.message || 'Failed to update shift', { variant: 'error' });
    }
  };

  const handleDeleteShift = async () => {
    if (!selectedShift) return;
    
    try {
      const response = await apiDelete(`/api/shifts/${selectedShift}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete shift');
      }
      
      setSelectedShift(null);
      setEditedShift(null);
      fetchShifts(); // Refresh shifts
    } catch (error) {
      console.error('Error deleting shift:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleAssignTherapist = async () => {
    if (!selectedTherapist || !selectedShift) {
      enqueueSnackbar('Please select both a therapist and a shift', { variant: 'error' });
      return;
    }

    try {
      const response = await apiPost('/api/shifts/assign', {
        therapist_id: selectedTherapist,
        shift_code: selectedShift,
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to assign therapist');
      }
      
      enqueueSnackbar('Therapist assigned successfully', { variant: 'success' });
      setOpenAssignDialog(false);
      setSelectedTherapist('');
      await fetchShiftTherapists();
      await fetchTherapists();
    } catch (error) {
      console.error('Error assigning therapist:', error);
      enqueueSnackbar(error.message || 'Failed to assign therapist', { variant: 'error' });
    }
  };

  const handleUnassignTherapist = async (therapistId) => {
    await apiDelete(`/api/shifts/unassign/${therapistId}`);
    fetchShiftTherapists();
    fetchTherapists();
  };

  const handleShiftSelect = (shiftCode) => {
    setSelectedShift(shiftCode);
    const shift = shifts.find(s => s.shift_code === shiftCode);
    if (shift) {
      // Map API breaks format (start_time, end_time) to frontend format (start, end)
      const mappedBreaks = Array.isArray(shift.breaks) 
        ? shift.breaks.map(br => ({
            start: br.start_time || br.start,
            end: br.end_time || br.end,
            break_type: br.break_type || 'BREAK',
            description: br.description || ''
          }))
        : [];
      
      setEditedShift({
        ...shift,
        breaks: mappedBreaks,
        working_days: Array.isArray(shift.working_days) ? shift.working_days : []
      });
    } else {
      setEditedShift(null);
    }
    setEditMode(false);
    const d = selectedDate?.format('YYYY-MM-DD');
    if (d) fetchAssignmentsForDate(d);
  };

  const handleAddBreak = () => {
    if (!editedShift) return;
    
    const breaks = Array.isArray(editedShift.breaks) ? editedShift.breaks : [];
    setEditedShift({
      ...editedShift,
      breaks: [...breaks, { start: '12:00', end: '12:30' }]
    });
  };

  const handleUpdateBreak = (index, field, value) => {
    if (!editedShift || !Array.isArray(editedShift.breaks)) return;
    
    const newBreaks = [...editedShift.breaks];
    newBreaks[index] = {
      ...newBreaks[index],
      [field]: value
    };
    
    setEditedShift({
      ...editedShift,
      breaks: newBreaks
    });
  };

  const handleDeleteBreak = (index) => {
    if (!editedShift || !Array.isArray(editedShift.breaks)) return;
    
    const newBreaks = editedShift.breaks.filter((_, i) => i !== index);
    setEditedShift({
      ...editedShift,
      breaks: newBreaks
    });
  };

  const handleWorkingDaysChange = (day) => {
    if (!editedShift) return;
    
    // Ensure working_days is always an array
    const currentDays = Array.isArray(editedShift.working_days) ? editedShift.working_days : [];
    
    // Toggle the day
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    
    // Update the shift with new working days
    setEditedShift(prev => ({
      ...prev,
      working_days: newDays
    }));
    
    console.log('Updated working days:', newDays); // Debug log
  };

  const handleUpdateWorkingDays = (event) => {
    const {
      target: { value },
    } = event;
    
    setEditedShift({
      ...editedShift,
      working_days: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleToggleWorkingDay = (day) => {
    if (!editedShift) return;
    
    const currentDays = Array.isArray(editedShift.working_days) 
      ? editedShift.working_days 
      : [];
    
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    
    setEditedShift({
      ...editedShift,
      working_days: newDays
    });
  };

  const handleAssignDialogClose = () => {
    setOpenAssignDialog(false);
    setSelectedTherapists([]);
  };

  const handleConfirmDialogClose = () => {
    setOpenConfirmDialog(false);
    setConflictingTherapists([]);
  };

  const handleTherapistSelect = (therapist) => {
    setSelectedTherapists(prev => {
      const isSelected = prev.some(t => t.name === therapist.name);
      if (isSelected) {
        return prev.filter(t => t.name !== therapist.name);
      } else {
        return [...prev, therapist];
      }
    });
  };

  const handleAssignTherapists = async () => {
    if (!selectedTherapists.length) return;

    // Check for conflicts
    const conflicts = [];
    for (const therapist of selectedTherapists) {
      const isAssigned = Object.entries(shiftTherapists).some(([code, therapists]) => 
        code !== selectedShift && therapists.some(t => t.name === therapist.name)
      );
      if (isAssigned) {
        conflicts.push(therapist);
      }
    }

    // If there are conflicts, show confirmation dialog
    if (conflicts.length > 0) {
      setConflictingTherapists(conflicts);
      setOpenConfirmDialog(true);
      return;
    }

    // If no conflicts, proceed with assignment
    await assignTherapistsToShift();
  };

  const assignTherapistsToShift = async (ignoreConflicts = false) => {
    try {
      // Filter out therapists that are already in the current shift
      const therapistsToAssign = selectedTherapists.filter(
        therapist => !shiftTherapists[selectedShift]?.some(t => t.name === therapist.name)
      );

      if (!therapistsToAssign.length) {
        handleAssignDialogClose();
        return;
      }

      // Assign therapists one by one using the existing endpoint
      for (const therapist of therapistsToAssign) {
        const response = await apiPost('/api/shifts/assign', {
          therapist_id: therapist.name,
          shift_code: selectedShift
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to assign therapist');
        }
      }

      enqueueSnackbar('Therapists assigned successfully', { variant: 'success' });
      handleAssignDialogClose();
      await fetchShiftTherapists();
    } catch (error) {
      console.error('Error assigning therapists:', error);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Map frontend breaks format (start, end) to API format (start_time, end_time)
      const apiBreaks = Array.isArray(editedShift.breaks)
        ? editedShift.breaks.map(br => ({
            start_time: br.start,
            end_time: br.end,
            break_type: br.break_type || 'BREAK',
            description: br.description || ''
          }))
        : [];
      
      const shiftData = {
        shift_code: editedShift.shift_code,
        name: editedShift.name,
        start_time: editedShift.start_time,
        end_time: editedShift.end_time,
        working_days: editedShift.working_days,
        color: editedShift.color || '#1976d2',
        breaks: apiBreaks,
        availability_type: editedShift.availability_type || 'AVAILABLE',
        description: editedShift.description || ''
      };

      const response = await apiPut('/api/shifts/${editedShift.shift_code}', shiftData);

      if (!response.ok) {
        throw new Error('Failed to update shift');
      }

      enqueueSnackbar('Shift updated successfully', { variant: 'success' });
      setEditMode(false); // Exit edit mode instead of using handleCloseDialog
      fetchShifts();
    } catch (error) {
      console.error('Error saving shift:', error);
      enqueueSnackbar('Error updating shift: ' + error.message, { variant: 'error' });
    }
  };

  const renderDialogActions = () => (
    <DialogActions>
      <Button onClick={() => setOpenNewShiftDialog(false)}>Cancel</Button>
      <Button 
        onClick={handleCreateShift}
        variant="contained" 
        color="primary"
        disabled={!newShift.name || !newShift.shift_code}
      >
        Create
      </Button>
    </DialogActions>
  );

  const renderEditActions = () => (
    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
      <Button onClick={() => setEditMode(false)}>Cancel</Button>
      <Button
        onClick={handleSaveChanges}
        variant="contained"
        color="primary"
        disabled={!editedShift?.name}
      >
        Save Changes
      </Button>
    </Box>
  );

  const renderWorkingDays = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">Working Days</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
          <Chip
            key={day}
            label={day}
            onClick={() => handleWorkingDaysChange(day)}
            color={editedShift?.working_days?.includes(day) ? "primary" : "default"}
            variant={editedShift?.working_days?.includes(day) ? "filled" : "outlined"}
            sx={{ m: 0.5 }}
          />
        ))}
      </Box>
    </Box>
  );

  const renderColorPicker = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">Color</Typography>
      <TextField
        label="Color"
        type="color"
        value={editedShift.color}
        onChange={(e) => setEditedShift({ ...editedShift, color: e.target.value })}
        sx={{
          '& input': {
            width: 100,
            height: 40,
            padding: '4px 8px'
          }
        }}
      />
    </Box>
  );

  const renderTherapistsPanel = () => {
    const selectedShiftData = shifts.find(s => s.shift_code === selectedShift);
    const dateStr = selectedDate.format('YYYY-MM-DD');
    
    // Get assignments for selected date and shift
    const dateAssignments = assignments[dateStr] || {};
    const shiftBlock = selectedShift ? dateAssignments[selectedShift] : null;
    const assignedTherapists = shiftBlock ? (shiftBlock.therapists || []) : [];
    
    console.log('Rendering therapists panel:', {
      selectedShift,
      dateStr,
      dateAssignments,
      assignedTherapists
    });

    return (
      <Box sx={{ p: 2, flex: 1, minWidth: 300, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Assigned Therapists</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedDate}
              onChange={(newDate) => setSelectedDate(newDate)}
              slotProps={{ textField: { size: 'small' } }}
              minDate={dayjs()}
            />
          </LocalizationProvider>
        </Box>
        
        {selectedShift ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ 
                color: selectedShiftData?.color,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box component="span" sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  backgroundColor: selectedShiftData?.color 
                }} />
                {selectedShiftData?.shift_code} - {selectedShiftData?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedShiftData?.start_time} - {selectedShiftData?.end_time}
              </Typography>
            </Box>

            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
              <Typography variant="body2" color="text.secondary">
                {assignedTherapists.length} therapists assigned for {dateStr}
              </Typography>
            </Box>

            <Box sx={{ overflow: 'auto', flex: 1 }}>
              {assignedTherapists.length > 0 ? (
                <Grid container spacing={1}>
                  {assignedTherapists.map((therapist) => (
                    <Grid item xs={12} key={therapist}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: alpha(selectedShiftData?.color || '#000', 0.02),
                          '&:hover': {
                            bgcolor: alpha(selectedShiftData?.color || '#000', 0.05),
                          },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          <Typography variant="body2">{therapist}</Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 200,
                  color: 'text.secondary',
                  gap: 1
                }}>
                  <PersonOffIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                  <Typography variant="body2">
                    No therapists assigned for this date
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            color: 'text.secondary',
            gap: 1
          }}>
            <ArrowBackIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography>
              Select a shift to view assigned therapists
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', gap: 2, p: 2, bgcolor: '#f6f6f6' }}>
      {/* Panel 1: Shifts List - 1/5 width */}
      <Paper sx={{ width: '20%', overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 2, p: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px', border: '1px solid #e5e7eb', bgcolor: '#fff' }}>
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: 64,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>Shifts</Typography>
          <IconButton 
            onClick={() => setOpenNewShiftDialog(true)} 
            color="primary"
            sx={{ 
              bgcolor: 'primary.lighter',
              '&:hover': { bgcolor: 'primary.light' }
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fff' }}>
          {shifts.map((shift) => (
            <ListItem
              key={shift.shift_code}
              button
              selected={selectedShift === shift.shift_code}
              onClick={() => handleShiftSelect(shift.shift_code)}
              sx={{
                mb: 1,
                borderRadius: 1.5,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                boxShadow: selectedShift === shift.shift_code ? 2 : 0,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                  transform: 'translateX(4px)',
                  boxShadow: 1,
                  transition: 'all 0.2s ease-in-out',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AccessTimeIcon 
                  color={selectedShift === shift.shift_code ? "primary" : "action"}
                  sx={{ 
                    fontSize: 20,
                    transition: 'color 0.2s ease-in-out',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={shift.name}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {shift.start_time} - {shift.end_time}
                    </Typography>
                    <Box 
                      component="span" 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        backgroundColor: shift.working_days?.includes(new Date().toLocaleString('en-US', {weekday: 'long'})) 
                          ? 'success.main' 
                          : 'text.disabled',
                        display: 'inline-block',
                        ml: 1,
                        transition: 'background-color 0.2s ease-in-out',
                      }} 
                    />
                  </Box>
                }
                primaryTypographyProps={{
                  variant: 'subtitle2',
                  fontWeight: 600,
                  sx: {
                    color: selectedShift === shift.shift_code ? 'primary.main' : 'text.primary',
                    transition: 'color 0.2s ease-in-out',
                    mb: 0.5,
                  }
                }}
                sx={{
                  my: 0.5,
                }}
              />
              <Box 
                sx={{ 
                  width: 4, 
                  height: '100%', 
                  position: 'absolute', 
                  left: 0, 
                  top: 0,
                  backgroundColor: selectedShift === shift.shift_code ? 'primary.main' : 'transparent',
                  transition: 'background-color 0.2s ease-in-out',
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                }}
              />
            </ListItem>
          ))}
        </Box>
      </Paper>

      {/* Panel 2: Shift Details - 2/5 width (40%) */}
      <Paper sx={{ width: '40%', overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 2, p: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px', border: '1px solid #e5e7eb', bgcolor: '#fff' }}>
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: 64,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>Shift Details</Typography>
          {selectedShift && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {editMode ? (
                <IconButton 
                  color="primary" 
                  onClick={handleUpdateShift}
                  sx={{ 
                    bgcolor: 'primary.lighter',
                    '&:hover': { bgcolor: 'primary.light' }
                  }}
                >
                  <SaveOutlinedIcon />
                </IconButton>
              ) : (
                <IconButton 
                  color="primary" 
                  onClick={() => setEditMode(true)}
                  sx={{ 
                    bgcolor: 'primary.lighter',
                    '&:hover': { bgcolor: 'primary.light' }
                  }}
                >
                  <EditOutlinedIcon />
                </IconButton>
              )}
              <IconButton 
                color="error" 
                onClick={() => handleDeleteShift(selectedShift)}
                sx={{ 
                  bgcolor: 'error.lighter',
                  '&:hover': { bgcolor: 'error.light' }
                }}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Box>
          )}
        </Box>
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          bgcolor: '#fff',
          p: 2 
        }}>
          {selectedShift && editedShift ? (
            <Stack spacing={3}>
              {editMode ? (
                <Stack spacing={3}>
                  <TextField
                    label="Shift Name"
                    value={editedShift.name}
                    onChange={(e) => setEditedShift({ ...editedShift, name: e.target.value })}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      }
                    }}
                  />
                  
                  {/* Availability Type Selector */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Availability Type
                    </Typography>
                    <ToggleButtonGroup
                      value={editedShift.availability_type || 'AVAILABLE'}
                      exclusive
                      onChange={(e, value) => {
                        if (value) {
                          setEditedShift({ ...editedShift, availability_type: value });
                        }
                      }}
                      fullWidth
                      size="small"
                    >
                      <ToggleButton value="AVAILABLE">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 18, color: (editedShift.availability_type || 'AVAILABLE') === 'AVAILABLE' ? 'success.main' : 'inherit' }} />
                          <Typography variant="body2">Available</Typography>
                        </Box>
                      </ToggleButton>
                      <ToggleButton value="UNAVAILABLE">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CancelIcon sx={{ fontSize: 18, color: editedShift.availability_type === 'UNAVAILABLE' ? 'error.main' : 'inherit' }} />
                          <Typography variant="body2">Unavailable</Typography>
                        </Box>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  
                  <TimePicker
                    label="Start Time"
                    value={dayjs(editedShift.start_time, 'HH:mm')}
                    onChange={(newValue) => setEditedShift({
                      ...editedShift,
                      start_time: newValue.format('HH:mm')
                    })}
                    slotProps={{ 
                      textField: { 
                        size: 'small',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          }
                        }
                      } 
                    }}
                  />
                  <TimePicker
                    label="End Time"
                    value={dayjs(editedShift.end_time, 'HH:mm')}
                    onChange={(newValue) => setEditedShift({
                      ...editedShift,
                      end_time: newValue.format('HH:mm')
                    })}
                    slotProps={{ 
                      textField: { 
                        size: 'small',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          }
                        }
                      } 
                    }}
                  />
                  
                  {renderWorkingDays()}
                  {renderColorPicker()}

                  {/* Breaks Section */}
                  <Box>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2 
                    }}>
                      <Typography variant="subtitle2" color="text.secondary">Breaks</Typography>
                      <IconButton 
                        size="small" 
                        onClick={handleAddBreak} 
                        color="primary"
                        sx={{ 
                          bgcolor: 'primary.lighter',
                          '&:hover': { bgcolor: 'primary.light' }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Stack spacing={2}>
                      {editedShift.breaks?.map((break_, index) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TimePicker
                              label="Start"
                              value={dayjs(break_.start, 'HH:mm')}
                              onChange={(newValue) => handleUpdateBreak(index, 'start', newValue.format('HH:mm'))}
                              slotProps={{ 
                                textField: { 
                                  size: 'small',
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                    }
                                  }
                                } 
                              }}
                            />
                            <TimePicker
                              label="End"
                              value={dayjs(break_.end, 'HH:mm')}
                              onChange={(newValue) => handleUpdateBreak(index, 'end', newValue.format('HH:mm'))}
                              slotProps={{ 
                                textField: { 
                                  size: 'small',
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                    }
                                  }
                                } 
                              }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteBreak(index)}
                              sx={{ 
                                bgcolor: 'error.lighter',
                                '&:hover': { bgcolor: 'error.light' }
                              }}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>

                  <TextField
                    label="Description"
                    multiline
                    rows={3}
                    value={editedShift.description}
                    onChange={(e) => setEditedShift({ ...editedShift, description: e.target.value })}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      }
                    }}
                  />
                  {renderEditActions()}
                </Stack>
              ) : (
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{editedShift.name}</Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Availability Type</Typography>
                    <Chip
                      icon={(editedShift.availability_type || 'AVAILABLE') === 'AVAILABLE' ? <CheckCircleIcon /> : <CancelIcon />}
                      label={(editedShift.availability_type || 'AVAILABLE') === 'AVAILABLE' ? 'Available' : 'Unavailable'}
                      color={(editedShift.availability_type || 'AVAILABLE') === 'AVAILABLE' ? 'success' : 'error'}
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Time</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {editedShift.start_time} - {editedShift.end_time}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Working Days</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {DAYS_OF_WEEK.map((day) => (
                        <Chip
                          key={day}
                          label={day}
                          size="small"
                          color={editedShift.working_days?.includes(day) ? "primary" : "default"}
                          variant={editedShift.working_days?.includes(day) ? "filled" : "outlined"}
                          sx={{ borderRadius: 1.5 }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Color</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: editedShift.color,
                          borderRadius: 1,
                          border: '1px solid rgba(0, 0, 0, 0.12)'
                        }}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Breaks</Typography>
                    <Stack spacing={1}>
                      {editedShift.breaks.map((break_, index) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{ 
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {break_.start} - {break_.end}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>

                  {editedShift.description && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                      <Typography variant="body1">{editedShift.description}</Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Assigned Therapists</Typography>
                    <Stack spacing={1}>
                      {(editedShift.assigned_therapists || []).map((therapist, index) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {therapist.name}
                          </Typography>
                          <Chip 
                            label={`Rank ${therapist.rank}`}
                            size="small"
                            color={therapist.rank === 1 ? "primary" : "default"}
                          />
                        </Paper>
                      ))}
                      {(!editedShift.assigned_therapists || editedShift.assigned_therapists.length === 0) && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No therapists assigned to this shift
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Stack>
          ) : (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 20 }} />
                Select a shift to view details
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Panel 3: Assigned Therapists - 2/5 width (40%) */}
      <Paper sx={{ width: '40%', overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 2, p: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px', border: '1px solid #e5e7eb', bgcolor: '#fff' }}>
        {renderTherapistsPanel()}
      </Paper>

      {/* New Shift Dialog */}
      <Dialog open={openNewShiftDialog} onClose={() => setOpenNewShiftDialog(false)} maxWidth="md">
        <DialogTitle sx={{ p: 3 }}>Create New Shift</DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Shift Name"
              value={newShift.name}
              onChange={(e) => setNewShift({ ...newShift, name: e.target.value })}
            />
            <TextField
              label="Shift Code"
              value={newShift.shift_code}
              onChange={(e) => setNewShift({ ...newShift, shift_code: e.target.value })}
            />
            
            {/* Availability Type Selector */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Availability Type
              </Typography>
              <ToggleButtonGroup
                value={newShift.availability_type}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    setNewShift({ ...newShift, availability_type: value });
                  }
                }}
                fullWidth
              >
                <ToggleButton value="AVAILABLE" sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: newShift.availability_type === 'AVAILABLE' ? 'success.main' : 'inherit' }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Available</Typography>
                      <Typography variant="caption" color="text.secondary">For appointments</Typography>
                    </Box>
                  </Box>
                </ToggleButton>
                <ToggleButton value="UNAVAILABLE" sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CancelIcon sx={{ color: newShift.availability_type === 'UNAVAILABLE' ? 'error.main' : 'inherit' }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Unavailable</Typography>
                      <Typography variant="caption" color="text.secondary">Leave/Off-duty</Typography>
                    </Box>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            <TimePicker
              label="Start Time"
              value={dayjs(newShift.start_time, 'HH:mm')}
              onChange={(newValue) => setNewShift({
                ...newShift,
                start_time: newValue.format('HH:mm')
              })}
            />
            <TimePicker
              label="End Time"
              value={dayjs(newShift.end_time, 'HH:mm')}
              onChange={(newValue) => setNewShift({
                ...newShift,
                end_time: newValue.format('HH:mm')
              })}
            />
            <TextField
              label="Description"
              multiline
              rows={2}
              value={newShift.description}
              onChange={(e) => setNewShift({ ...newShift, description: e.target.value })}
            />
            <TextField
              label="Color"
              type="color"
              value={newShift.color}
              onChange={(e) => setNewShift({ ...newShift, color: e.target.value })}
              sx={{
                '& input': {
                  width: 100,
                  height: 40,
                  padding: '4px 8px'
                }
              }}
            />
          </Box>
        </DialogContent>
        {renderDialogActions()}
      </Dialog>

      {/* Assign Therapists Drawer */}
      <Drawer
        anchor="right"
        open={openAssignDialog}
        onClose={handleAssignDialogClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '20%',
            boxSizing: 'border-box',
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
          },
        }}
      >
        <Box sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column'
        }}>
          {/* Header */}
          <Box sx={{ 
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Add Therapists</Typography>
            <IconButton onClick={handleAssignDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Box */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search therapists..."
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                sx: { borderRadius: 1.5 }
              }}
            />
          </Box>

          {/* Therapist List */}
          <List sx={{ 
            flex: 1, 
            overflow: 'auto',
            px: 1,
            py: 1.5,
          }}>
            {therapists.map((therapist) => {
              const isAssigned = shiftTherapists[selectedShift]?.some(t => t.name === therapist.name);
              return (
                <ListItem
                  key={therapist.name}
                  disablePadding
                  sx={{
                    mb: 0.5,
                  }}
                >
                  <ListItemButton
                    onClick={() => handleTherapistSelect(therapist)}
                    disabled={isAssigned}
                    dense
                    sx={{
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        opacity: 0.7,
                        bgcolor: 'action.disabledBackground'
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedTherapists.some(t => t.name === therapist.name)}
                        disabled={isAssigned}
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {therapist.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Rank: {therapist.rank}
                          </Typography>
                          {isAssigned && (
                            <Chip
                              label="Assigned"
                              size="small"
                              color="default"
                              sx={{ 
                                height: 16,
                                '& .MuiChip-label': {
                                  px: 1,
                                  fontSize: '0.625rem',
                                }
                              }}
                            />
                          )}
                        </Box>
                      }
                      sx={{ my: 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          {/* Action Buttons */}
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAssignTherapists}
              disabled={!selectedTherapists.length}
            >
              Assign Selected ({selectedTherapists.length})
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Confirmation Dialog for Conflicts */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleConfirmDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
            Therapist Assignment Conflicts
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The following therapists are already assigned to other shifts:
            </Typography>
            <List sx={{ 
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              {conflictingTherapists.map((therapist) => (
                <ListItem key={therapist.name}>
                  <ListItemText
                    primary={therapist.name}
                    secondary={`Rank: ${therapist.rank}`}
                  />
                </ListItem>
              ))}
            </List>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Would you like to move these therapists to the current shift or cancel the operation?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleConfirmDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => assignTherapistsToShift(true)}
          >
            Move Therapists
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftManagement;
